import React, { useState, useEffect, useRef } from 'react';

interface VariableInputProps {
    variable: any;
    isForCurrentUser: boolean;
    onUpdate: (value: string) => void;
}

const VariableInput: React.FC<VariableInputProps> = ({ variable, isForCurrentUser, onUpdate }) => {
    // Initialize local state from prop
    const [value, setValue] = useState(variable.value || '');

    // Track the last value we sent to parent to avoid re-sync loops
    const lastSentValueRef = useRef<string>(variable.value || '');

    // Track if this is the first mount
    const isFirstMountRef = useRef(true);

    // Sync local state if prop changes externally (e.g. initial load or reset)
    // But ignore if the change came from our own onUpdate call
    useEffect(() => {
        const incomingValue = variable.value || '';

        // On first mount, always sync
        if (isFirstMountRef.current) {
            isFirstMountRef.current = false;
            setValue(incomingValue);
            lastSentValueRef.current = incomingValue;
            return;
        }

        // Only sync if the incoming value is different from what we last sent
        // This means it's a true external update (like a reset)
        if (incomingValue !== lastSentValueRef.current) {
            setValue(incomingValue);
            lastSentValueRef.current = incomingValue;
        }
    }, [variable.value]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setValue(e.target.value);
    };

    const handleBlur = () => {
        // Only update parent if value has changed from what parent knows
        if (value !== (variable.value || '')) {
            lastSentValueRef.current = value;
            onUpdate(value);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                    {variable.name}
                </label>
            </div>
            <textarea
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={`Masukkan nilai untuk {${variable.name}}`}
                rows={3}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-y ${isForCurrentUser
                    ? 'border-gray-300'
                    : 'border-gray-200 bg-gray-50'
                    }`}
                disabled={!isForCurrentUser}
            />
            {!isForCurrentUser && variable.group_id && (
                <p className="text-xs text-gray-500 mt-1">
                    Akan diisi oleh group lain
                </p>
            )}
        </div>
    );
};

export default React.memo(VariableInput);
